import React, { useMemo } from 'react';
import { isNum, stringifyWithNull } from '../../utils/primitives';
import Delta from 'quill-delta';
import useTextEdit from './useTextEdit';
import { fieldValues, initInfo, initState } from '../../utils/init';
import { ACTION_NEXT } from '../../utils/elementActions';
import { formatNumberValue } from '../fields/TextField/mask';
import { getOptionLabel } from '../../utils/optionLabels';

export const TEXT_VARIABLE_PATTERN = /{{.*?}}/g;

/**
 * Renders one interpolated value. Number fields that opted into showing their
 * format carry their units and precision here, option fields render the label
 * their value stands for, and everything else stringifies exactly as it always
 * has.
 */
function renderValue(
  key: string,
  value: any,
  repeat?: number,
  useOptionLabels?: boolean
) {
  const servar = initState.textVariableFormats[key];
  if (servar) return formatNumberValue(servar, value);
  const label = useOptionLabels
    ? getOptionLabel(key, value, repeat)
    : undefined;
  return label ?? stringifyWithNull(value);
}

/**
 * `useOptionLabels` is opt-in because this function serves two audiences. Text
 * a user reads should show an option's label, but the same interpolation builds
 * redirect URLs, iframe sources and envelope file names, where the stored value
 * is usually a code that another system expects verbatim. Display call sites
 * pass true; those machine-facing ones leave it off.
 */
export function replaceTextVariables(
  text: string,
  repeat?: any,
  useOptionLabels = false,
  transformValue?: (key: string, value: unknown) => unknown
) {
  if (!text) return '';

  return text.replace(TEXT_VARIABLE_PATTERN, (pattern: any) => {
    const pStr = pattern.slice(2, -2);
    if (pStr === 'feathery_user_id') return initInfo().userId;
    if (pStr in fieldValues) {
      const pVal = transformValue
        ? transformValue(pStr, fieldValues[pStr])
        : fieldValues[pStr];
      if (Array.isArray(pVal)) {
        if (pVal.length === 0) {
          return '';
        } else if (isNaN(repeat)) {
          // Each entry's position is its repeat index, which is what selects
          // per-index options for a repeating field.
          return pVal
            .map((entry, i) => renderValue(pStr, entry, i, useOptionLabels))
            .join(', ');
        } else if (repeat >= pVal.length) {
          return renderValue(pStr, pVal[0], 0, useOptionLabels);
        } else {
          return renderValue(pStr, pVal[repeat], repeat, useOptionLabels);
        }
      } else return renderValue(pStr, pVal, undefined, useOptionLabels);
    }
    // A real field the user hasn't filled renders empty, while a name that
    // matches no field stays literal so authors see what they typed.
    return initState.knownFieldKeys.has(pStr) ? '' : pattern;
  });
}

const applyNewDelta = (
  delta: any,
  start?: number | undefined,
  end?: number | undefined
) => {
  if (start !== undefined && end !== undefined)
    return delta.compose(
      new Delta().retain(start).retain(end - start, { start, end })
    );
  else
    return delta.compose(
      new Delta().retain(delta.length(), { fullSpan: true })
    );
};

function TextNode({
  index,
  cursor,
  onClick = () => {},
  fontStyles,
  text,
  link,
  editMode
}: any) {
  const styles = {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    cursor,
    ...fontStyles
  };
  return link && !editMode ? (
    <a
      data-index={index}
      css={styles}
      href={link}
      target='_blank'
      rel='noreferrer'
    >
      {text}
    </a>
  ) : (
    <span data-index={index} css={styles} onClick={onClick}>
      {text}
    </span>
  );
}

function TextNodes({
  element,
  responsiveStyles,
  cssTarget = '',
  conditions = [],
  editMode,
  disabled = false,
  focused = false,
  textSpanOnClick = () => {},
  hasClickLogic = false,
  textCallbacks = {},
  featheryContext = {},
  expand = true
}: any) {
  const { spanRef, editableProps } = useTextEdit({
    editable: editMode === 'editable',
    focused,
    expand,
    ...textCallbacks
  });

  // Empty labels stay mounted in the builder so they can be typed into, but
  // take no space until selected so the canvas matches the published form.
  const collapseEmptyLabel =
    editMode === 'editable' && !focused && !element.properties.text;

  editableProps.css = {
    ...editableProps.css,
    ...responsiveStyles.getTarget(cssTarget),
    ...(collapseEmptyLabel ? { minWidth: 0, paddingBottom: 0 } : {})
  };

  // Not using jsonpath because of issues with NextJS
  const extractProperty = (obj: any, path: string[]): any => {
    if (path.length === 0) return obj;
    const [key, ...rest] = path;
    if (obj[key] === undefined) return null;
    return extractProperty(obj[key], rest);
  };

  return useMemo(() => {
    const text = element.properties.text;
    let delta = new Delta(element.properties.text_formatted);
    const actions = element.properties.actions ?? [];
    if (actions.some((action: any) => action.type === ACTION_NEXT)) {
      conditions.forEach((cond: any) => {
        if (cond.element_type === 'text' && cond.element_id === element.id) {
          const start = cond.metadata.start;
          const end = cond.metadata.end;
          delta = applyNewDelta(delta, start, end);
        }
      });
    } else if (actions.length > 0 || hasClickLogic) {
      // Text with no click actions is still clickable if a logic rule runs on click
      delta = applyNewDelta(delta);
    }

    // If text_mode property is set to 'data', then we don't want to render the text_formatted
    // property, instead we the text from the data element specified in the text_source property
    let textFromData = null;

    if (element.properties.text_mode === 'data') {
      let textSource = element.properties.text_source ?? '';
      // convert to path relative to featheryContext
      if (textSource.startsWith('feathery.'))
        textSource = textSource.replace('feathery.', '');
      textFromData = extractProperty(featheryContext, textSource.split('.'));
    }
    const textIsFromData =
      element.properties.text_mode === 'data' && textFromData !== null;

    return (
      <span
        id={`span-${element.id}`}
        ref={spanRef}
        {...editableProps}
        key={text}
      >
        {textIsFromData ? (
          <TextNode
            index={0}
            {...(hasClickLogic && !editMode && !disabled
              ? {
                  // Data-sourced text has no spans, so the whole text is the
                  // click target for its logic rule
                  onClick: () => textSpanOnClick(undefined, undefined),
                  cursor: 'pointer'
                }
              : { cursor: 'inherit' })}
            fontStyles={responsiveStyles.getRichFontStyles(
              element.properties?.text_formatted[0]?.attributes ?? {}
            )}
            text={textFromData}
            editMode={editMode}
          />
        ) : (
          delta
            .filter((op) => !!op.insert)
            .map((op, i) => {
              const attrs = op.attributes || {};
              let onClick = () => {};
              let cursor = 'inherit';
              let link = '';
              if (!editMode && !disabled) {
                if (attrs.font_link) {
                  link = replaceTextVariables(attrs.font_link, element.repeat);
                  cursor = 'pointer';
                } else if (
                  attrs.fullSpan ||
                  (isNum(attrs.start) && isNum(attrs.end))
                ) {
                  onClick = () => textSpanOnClick(attrs.start, attrs.end);
                  cursor = 'pointer';
                }
              }

              const text = editMode
                ? (op.insert as string)
                : replaceTextVariables(
                    op.insert as string,
                    element.repeat,
                    true
                  );

              return (
                <TextNode
                  key={i}
                  index={i}
                  cursor={cursor}
                  fontStyles={responsiveStyles.getRichFontStyles(attrs)}
                  onClick={onClick}
                  text={text}
                  link={link}
                  editMode={editMode}
                />
              );
            })
        )}
      </span>
    );
  }, [element, responsiveStyles, editableProps, hasClickLogic]);
}

export default TextNodes;
